import { useEffect, useState } from "react";
import { useDataProvider, useGetIdentity, useRecordContext } from "react-admin";
import { get } from "lodash";
import { Typography } from "@mui/material";
import { deriveTimestampsFromDate } from "src/utils/dateTimeUtils";

interface EventCountFieldProps {
  source: string;
  label: string;
  includeFilters: {
    namespace: string;
    category: string;
    enum?: string;
  }[];
}

type EventFilter = EventCountFieldProps["includeFilters"][number];

// Kept separate from the field so the CSV export makes the same request and
// applies the same 90-day filter as the visible cell.
type EventCountDataProvider = {
  getManyReference: (
    resource: string,
    params: unknown
  ) => Promise<{ data: { duration: { startTime: number } }[] }>;
};

export const getEventCount = async (
  dataProvider: unknown,
  patientId: string | number,
  includeFilters: EventFilter[],
  canSeePII: boolean | undefined
): Promise<number> => {
  const eventsDataProvider = dataProvider as EventCountDataProvider;
  const now = new Date();
  const startDate = new Date(now);
  startDate.setDate(now.getDate() - 90);
  const { startDateTimestamp } = deriveTimestampsFromDate(startDate);
  const eventData = await eventsDataProvider.getManyReference("PatientEvent", {
    target: "patient",
    id: patientId,
    pagination: { page: 1, perPage: 100 },
    sort: { field: "createdAt", order: "DESC" },
    filter: {},
    meta: { enablePiiToggle: canSeePII, includeFilters }
  });
  return eventData.data.filter(
    (event: { duration: { startTime: number } }) =>
      event.duration.startTime > startDateTimestamp
  ).length;
};

const EventCountField = (props: EventCountFieldProps) => {
  const { includeFilters } = props;
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const { data: user } = useGetIdentity();
  const canSeePII = user?.defaultMembership?.role.canSeePHI;

  const patientId = get(record, "id");
  const [count, setCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchEvents = async () => {
      const eventCount = await getEventCount(
        dataProvider,
        patientId,
        includeFilters,
        canSeePII
      );
      if (!cancelled) {
        setCount(eventCount);
        setLoading(false);
      }
    };

    fetchEvents().catch((err) => {
      console.error("Error fetching event count:", err);
      if (!cancelled) {
        setCount(0);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [dataProvider, patientId]);

  if (loading) {
    return <Typography variant="body2">Loading...</Typography>;
  }

  return (
    <Typography variant="body2">
      {count != null && count > 0 ? `${count} in 90 days` : "None"}
    </Typography>
  );
};

export default EventCountField;
